import t from '@jetshop/intl';
import React, { useState } from 'react';
import { Query } from 'react-apollo';
import { styled } from 'linaria/react';
import { fuzzySearch } from '@jetshop/ui/fuzzySearch';
import { Above } from '@jetshop/ui/Breakpoints';
import { distance } from '@jetshop/core/helpers/distance';

import MaxWidth from '../Layout/MaxWidth';
import LoadingPage from '../LoadingPage';
import storeLocatorQuery from './StoreLocator.gql';
import StoreSearch from './StoreSearch';
import StoreList from './StoreList';
import StoreMap from './StoreMap';
import { useLocationState } from './useLocationState';
import Head from '@jetshop/core/components/Head';
import { Intl } from '@jetshop/intl';

import { theme } from '../Theme';
import UserLocation from './UserLocation';
import { DynamicCategoryRenderer } from '../ContentEditor/DynamicCategoryRenderer/DynamicCategoryRenderer';
import { allContentComponents } from '../ContentEditor/ContentComponents';

const Flex = styled('div')`
  display: flex;

  /* h1 {
    margin-bottom: 0.5em;
  } */

  /* > div {
    flex: 1;
  } */

  ${theme.below.sm} {
    flex-direction: column;
  }
`;

const StoreLocatorWrapper = styled(MaxWidth)`
  max-width: 100vw;
  overflow: hidden;
  padding: 0;
  /* margin-bottom: 100px; */
  ${theme.below.md} {
    flex-direction: column;
  }
`;

const StoreLocatorContent = styled(Flex)`
  height: 60vh;

  ${theme.below.lg} {
    height: 90vh;
  }
  ${theme.below.md} {
    height: unset;
  }
`;

const SearchAndListContainer = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  ${theme.below.lg} {
    max-width: 50%;
  }
  ${theme.below.md} {
    max-width: 100%;
  }
  h1 {
    font-style: normal;
    font-weight: normal;
    font-size: 28px;
    line-height: 20px;
    text-align: center;
    text-transform: uppercase;
  }
`;

const InnerWrapper = styled('div')`
  text-align: center;
  width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  margin: 10px auto;

  .inner-scroll {
    height: 100%;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding-bottom: 20px;
  }

  ${theme.below.lg} {
    display: flex;
    flex-direction: column;
  }
`;

const MobileWrapper = styled('div')`
  padding: 10px 20px;

  h1 {
    text-align: center;
    font-size: 28px;
  }
`;

export const LocationStateContext = React.createContext(null);

const StoreLocator = () => {
  const locationState = useLocationState();
  const [search, setSearch] = useState('');
  const [userLocation, setUserLocation] = useState({
    latitude: null,
    longitude: null
  });
  const [closestStore, setClosestStore] = useState(null);
  const [distances, setDistances] = useState({});
  const [clickedStore, setClickedStore] = useState(null);

  const genDistances = stores => {
    if (userLocation.latitude == null) return;

    let distances = {};
    let closestStore = {};
    stores
      .filter(store => store.coordinates)
      .forEach(store => {
        const thisDistance = distance(userLocation, store.coordinates);

        distances[store.id] = thisDistance;

        // If we haven't got a store in the closestStore obj yet, set it to this one
        if (!closestStore.distance) {
          closestStore = {
            ...store,
            distance: thisDistance
          };
          return;
        }

        // If this store is closer than the one currently stored in closestStore,
        // override it
        if (thisDistance < closestStore.distance) {
          closestStore = { ...store, distance: thisDistance };
        }
      });

    setDistances(distances);
    setClosestStore(closestStore);
  };

  return (
    <>
      <Intl>{t => <Head data={{ title: t('Find store') }} />}</Intl>
      <div>
        <DynamicCategoryRenderer
          categoryId={process?.env?.REACT_APP_RESELLERS_ID}
          rendererComponents={allContentComponents}
        />
      </div>
      <StoreLocatorWrapper>
        <StoreLocatorContent>
          <Query query={storeLocatorQuery}>
            {({ data, loading }) => {
              if (loading) return <LoadingPage />;

              if (userLocation.latitude && !closestStore)
                genDistances(data.stores);

              const stores =
                search.length === 0
                  ? data.stores
                  : fuzzySearch({
                      data: data.stores,
                      keys: ['name', 'address1'],
                      searchTerm: search
                    });

              const storeSearchProps = { search, setSearch };
              const storeListProps = {
                stores,
                userLocation,
                setUserLocation,
                distances,
                clickedStore,
                setClickedStore
              };
              const storeMapProps = {
                stores,
                userLocation,
                closestStore,
                distances,
                clickedStore,
                setClickedStore
              };

              return (
                <LocationStateContext.Provider value={locationState}>
                  <Above breakpoint="lg">
                    {matches =>
                      matches ? (
                        <>
                          <StoreMap {...storeMapProps} />
                          <InnerWrapper>
                            <h1>{t('Find retailer')}</h1>
                            <SearchAndListContainer>
                              <StoreSearch {...storeSearchProps} />
                              <UserLocation setUserLocation={setUserLocation} />
                            </SearchAndListContainer>
                            <div className="inner-scroll">
                              <StoreList {...storeListProps} />
                            </div>
                          </InnerWrapper>
                        </>
                      ) : (
                        <>
                          <StoreMap {...storeMapProps} />
                          <MobileWrapper>
                            <h1>{t('Find retailer')}</h1>
                            <InnerWrapper>
                              <StoreSearch {...storeSearchProps} />
                              <UserLocation setUserLocation={setUserLocation} />
                              <StoreList {...storeListProps} />
                            </InnerWrapper>
                          </MobileWrapper>
                        </>
                      )
                    }
                  </Above>
                </LocationStateContext.Provider>
              );
            }}
          </Query>
        </StoreLocatorContent>
      </StoreLocatorWrapper>
    </>
  );
};

export default StoreLocator;
